import React, { useState, useEffect, memo } from 'react';
import { auth, db } from '../firebase';
import Menu from '../main/menu';
import './styles/users.css';
import { 
  doc, 
  collection, 
  getDocs, 
  setDoc,
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword, 
  sendPasswordResetEmail 
} from 'firebase/auth';

// Separat Modal komponent
const CreateUserModal = memo(({ onClose, onSubmit, initialValues }) => {
  const [formData, setFormData] = useState(initialValues);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="adm-modal-overlay">
      <div className="adm-modal">
        <div className="adm-modal-header">
          <h3>Opret ny bruger</h3>
          <button 
            className="adm-modal-close"
            onClick={onClose}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="adm-modal-form">
          <div className="adm-form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className="adm-input"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
            />
          </div>
          <div className="adm-form-group">
            <label htmlFor="name">Navn</label>
            <input
              id="name"
              type="text"
              className="adm-input"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          </div>
          <div className="adm-form-group">
            <label htmlFor="company">Virksomhed</label>
            <input
              id="company"
              type="text"
              className="adm-input"
              value={formData.company}
              onChange={(e) => handleChange('company', e.target.value)}
              required
            />
          </div>
          <div className="adm-form-group">
            <label className="adm-system-toggle">
              <input
                type="checkbox"
                checked={formData.linkedInPoster}
                onChange={(e) => handleChange('linkedInPoster', e.target.checked)}
              />
              <span className="adm-system-slider"></span>
            </label>
            <span className="adm-system-name">LinkedIn Poster</span>
          </div>
          {formData.linkedInPoster && (
            <div className="adm-form-group">
              <label htmlFor="weeklyPosts">Antal ugentlige opslag</label>
              <input
                id="weeklyPosts"
                type="number"
                className="adm-input"
                value={formData.weeklyPosts}
                onChange={(e) => handleChange('weeklyPosts', e.target.value)}
                min="0"
                max="100"
              />
            </div>
          )}
          <div className="adm-modal-actions">
            <button 
              type="button" 
              className="adm-modal-cancel"
              onClick={onClose}
            >
              Annuller
            </button>
            <button type="submit" className="adm-modal-submit">
              Opret bruger
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

const Users = ({ onLogout }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    company: '',
    linkedInPoster: false,
    weeklyPosts: 0
  });

  // Hent brugere når komponenten indlæses
  useEffect(() => {
    fetchUsers();
  }, []);

  // Hent alle brugere fra Firestore
  const fetchUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Fejl ved hentning af brugere:', error);
      showMessage('error', 'Der opstod en fejl ved hentning af brugere');
    } finally {
      setLoading(false);
    }
  };

  // Vis besked til brugeren
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // Opret ny bruger
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Opret bruger i Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newUser.email,
        Math.random().toString(36).slice(-8) // Midlertidigt kodeord
      );

      // Opret brugerdokument i Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: newUser.email,
        name: newUser.name,
        company: newUser.company,
        role: 'user',
        systems: {
          linkedInPoster: newUser.linkedInPoster,
          weeklyPosts: parseInt(newUser.weeklyPosts) || 0
        },
        createdAt: serverTimestamp()
      });

      // Send mail til nulstilling af kodeord
      await sendPasswordResetEmail(auth, newUser.email);

      showMessage('success', 'Bruger oprettet og velkomstmail sendt');
      setModalOpen(false);
      setNewUser({
        email: '',
        name: '',
        company: '',
        linkedInPoster: false,
        weeklyPosts: 0
      });
      fetchUsers();
    } catch (error) {
      console.error('Fejl ved oprettelse af bruger:', error);
      showMessage('error', 'Der opstod en fejl ved oprettelse af brugeren');
    } finally {
      setLoading(false);
    }
  };

  // Opdater brugerindstillinger
  const handleUpdateUser = async (userId, updates) => {
    try {
      await updateDoc(doc(db, 'users', userId), updates);
      showMessage('success', 'Brugerindstillinger opdateret');
      fetchUsers();
    } catch (error) {
      console.error('Fejl ved opdatering af bruger:', error);
      showMessage('error', 'Der opstod en fejl ved opdatering af brugeren');
    }
  };

  return (
    <div className="dashboard-layout">
      <Menu onLogout={onLogout} />
      
      <main className="dashboard-content">
        <div className="adm-page-header">
          <h1 className="adm-page-title">Brugeradministration</h1>
          <button 
            className="adm-create-button"
            onClick={() => setModalOpen(true)}
          >
            <i className="fas fa-plus"></i>
            Opret ny bruger
          </button>
        </div>

        {loading ? (
          <div className="adm-loading">
            <i className="fas fa-spinner fa-spin"></i> Indlæser...
          </div>
        ) : (
          <div className="adm-users-grid">
            {users.map(user => (
              <div key={user.id} className="adm-user-card">
                <div className="adm-user-header">
                  <div className="adm-user-info">
                    <div className="adm-user-avatar">
                      <i className="fas fa-user"></i>
                    </div>
                    <div className="adm-user-details">
                      <h3 className="adm-user-name">{user.name}</h3>
                      <p className="adm-user-company">{user.company}</p>
                      <p className="adm-user-email">{user.email}</p>
                    </div>
                  </div>
                  <span className={`adm-user-role ${user.role}`}>
                    {user.role === 'admin' ? 'Administrator' : 'Bruger'}
                  </span>
                </div>
                
                <div className="adm-user-systems">
                  <div className="adm-system-item">
                    <div className="adm-system-header">
                      <span className="adm-system-name">LinkedIn Poster</span>
                      <label className="adm-system-toggle">
                        <input
                          type="checkbox"
                          checked={user.systems?.linkedInPoster || false}
                          onChange={(e) => handleUpdateUser(user.id, {
                            'systems.linkedInPoster': e.target.checked
                          })}
                        />
                        <span className="adm-system-slider"></span>
                      </label>
                    </div>
                    {user.systems?.linkedInPoster && (
                      <div className="adm-posts-input">
                        <label>Ugentlige opslag:</label>
                        <input
                          type="number"
                          value={user.systems.weeklyPosts || 0}
                          onChange={(e) => handleUpdateUser(user.id, {
                            'systems.weeklyPosts': parseInt(e.target.value) || 0
                          })}
                          min="0"
                          max="100"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {modalOpen && (
          <CreateUserModal
            onClose={() => setModalOpen(false)}
            onSubmit={handleCreateUser}
            initialValues={newUser}
          />
        )}
        
        {message.text && (
          <div className={`adm-message ${message.type}`}>
            {message.text}
          </div>
        )}
      </main>
    </div>
  );
};

export default Users;
